/**
 * Enhanced Invoice Dashboard Component
 * Main dashboard for enhanced invoice management
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNotifications } from '../common/NotificationSystem';
import { enhancedInvoiceService } from '../../services/enhancedInvoiceService';
import { useRBAC } from '../../hooks/useRBAC';
import ProfessionalLoader from "../common/ProfessionalLoader";
import BulkInvoiceOperations from './BulkInvoiceOperations';
import InvoiceTemplates from './InvoiceTemplates';
import './EnhancedInvoiceDashboard.css';

const EnhancedInvoiceDashboard = () => {
  const { showError } = useNotifications();
  const { canManageInvoices } = useRBAC();

  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const loadDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await enhancedInvoiceService.getInvoiceDashboard();

      if (response.success) {
        setDashboardData(response.data);
      } else {
        showError(response.message || 'Failed to load dashboard data', 'Error');
      }
    } catch (error) {
      showError('Failed to load dashboard data', 'Error');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);


  if (isLoading) {
    return (
      <div className="enhanced-invoice-dashboard">
        <div className="loading-container">
          <ProfessionalLoader size="large" variant="default" />
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="enhanced-invoice-dashboard">
        <div className="error-state">
          <h3>Failed to Load Dashboard</h3>
          <p>Unable to load enhanced invoice dashboard data.</p>
          <button className="btn btn-primary" onClick={loadDashboardData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const {
    invoice_statistics,
    xero_sync_statistics,
    recent_invoices,
    overdue_invoices,
    system_status
  } = dashboardData;

  return (
    <div className="enhanced-invoice-dashboard">
      {/* Header */}
      <div className="enhanced-invoice-dashboard-header">
        <div className="header-content">
          <h2>📊 Enhanced Invoice Dashboard</h2>
          <p>Manage invoices, bulk operations, and Xero integration</p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-outline"
            onClick={() => setActiveTab('templates')}
            disabled={!canManageInvoices}
          >
            📋 Templates
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setActiveTab('bulk-operations')}
            disabled={!canManageInvoices}
          >
            🔄 Bulk Operations
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="dashboard-tabs">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button
          className={`tab-button ${activeTab === 'bulk-operations' ? 'active' : ''}`}
          onClick={() => setActiveTab('bulk-operations')}
        >
          🔄 Bulk Operations
        </button>
        <button
          className={`tab-button ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          📋 Templates
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            {/* Invoice Statistics */}
            <div className="statistics-section">
              <h3>Invoice Statistics</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">📊</div>
                  <div className="stat-content">
                    <h4>Total Invoices</h4>
                    <p className="stat-value">{invoice_statistics.total_invoices}</p>
                  </div>
                </div>

                <div className="stat-card draft">
                  <div className="stat-icon">📝</div>
                  <div className="stat-content">
                    <h4>Draft</h4>
                    <p className="stat-value">{invoice_statistics.draft_invoices}</p>
                  </div>
                </div>

                <div className="stat-card sent">
                  <div className="stat-icon">📤</div>
                  <div className="stat-content">
                    <h4>Sent</h4>
                    <p className="stat-value">{invoice_statistics.sent_invoices}</p>
                  </div>
                </div>

                <div className="stat-card paid">
                  <div className="stat-icon">✅</div>
                  <div className="stat-content">
                    <h4>Paid</h4>
                    <p className="stat-value">{invoice_statistics.paid_invoices}</p>
                  </div>
                </div>

                <div className="stat-card overdue">
                  <div className="stat-icon">⚠️</div>
                  <div className="stat-content">
                    <h4>Overdue</h4>
                    <p className="stat-value">{invoice_statistics.overdue_invoices}</p>
                  </div>
                </div>

                <div className="stat-card amount">
                  <div className="stat-icon">💰</div>
                  <div className="stat-content">
                    <h4>Total Amount</h4>
                    <p className="stat-value">${invoice_statistics.total_amount?.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Xero Sync Statistics */}
            <div className="sync-section">
              <h3>Xero Sync Statistics</h3>
              <div className="sync-cards">
                <div className="sync-card">
                  <div className="sync-icon">🔄</div>
                  <div className="sync-content">
                    <h4>Synced</h4>
                    <p className="sync-value">{xero_sync_statistics.synced_invoices}</p>
                  </div>
                </div>

                <div className="sync-card failed">
                  <div className="sync-icon">❌</div>
                  <div className="sync-content">
                    <h4>Failed</h4>
                    <p className="sync-value">{xero_sync_statistics.failed_sync_invoices}</p>
                  </div>
                </div>

                <div className="sync-card pending">
                  <div className="sync-icon">⏳</div>
                  <div className="sync-content">
                    <h4>Pending</h4>
                    <p className="sync-value">{xero_sync_statistics.pending_sync_invoices}</p>
                  </div>
                </div>

                <div className="sync-card success-rate">
                  <div className="sync-icon">📈</div>
                  <div className="sync-content">
                    <h4>Success Rate</h4>
                    <p className="sync-value">{xero_sync_statistics.sync_success_rate}%</p>
                  </div>
                </div>
              </div>
            </div>

            {/* System Status */}
            <div className="system-status-section">
              <h3>System Status</h3>
              <div className="status-cards">
                <div className={`status-card ${system_status.xero_configured ? 'configured' : 'not-configured'}`}>
                  <div className="status-icon">
                    {system_status.xero_configured ? '✅' : '❌'}
                  </div>
                  <div className="status-content">
                    <h4>Xero Configuration</h4>
                    <p>{system_status.xero_configured ? 'Configured' : 'Not Configured'}</p>
                  </div>
                </div>

                <div className={`status-card ${system_status.xero_connected ? 'connected' : 'disconnected'}`}>
                  <div className="status-icon">
                    {system_status.xero_connected ? '✅' : '❌'}
                  </div>
                  <div className="status-content">
                    <h4>Xero Connection</h4>
                    <p>{system_status.xero_connected ? 'Connected' : 'Disconnected'}</p>
                  </div>
                </div>

                <div className={`status-card ${system_status.celery_running ? 'running' : 'stopped'}`}>
                  <div className="status-icon">
                    {system_status.celery_running ? '✅' : '❌'}
                  </div>
                  <div className="status-content">
                    <h4>Background Tasks</h4>
                    <p>{system_status.celery_running ? 'Running' : 'Stopped'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Invoices */}
            <div className="recent-invoices-section">
              <h3>Recent Invoices</h3>
              <div className="invoices-list">
                {recent_invoices.length > 0 ? (
                  recent_invoices.map(invoice => (
                    <div key={invoice.id} className="invoice-item">
                      <div className="invoice-info">
                        <span className="invoice-number">{invoice.invoice_number}</span>
                        <span className="invoice-member">{invoice.member_name}</span>
                      </div>
                      <div className="invoice-details">
                        <span className="invoice-amount">${invoice.amount}</span>
                        <span className={`invoice-status ${invoice.status}`}>{invoice.status}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-invoices">
                    <p>No recent invoices found</p>
                  </div>
                )}
              </div>
            </div>

            {/* Overdue Invoices */}
            {overdue_invoices.length > 0 && (
              <div className="overdue-invoices-section">
                <h3>Overdue Invoices</h3>
                <div className="overdue-list">
                  {overdue_invoices.map(invoice => (
                    <div key={invoice.id} className="overdue-item">
                      <div className="overdue-info">
                        <span className="overdue-number">{invoice.invoice_number}</span>
                        <span className="overdue-member">{invoice.member_name}</span>
                        <span className="overdue-days">{invoice.days_overdue} days overdue</span>
                      </div>
                      <div className="overdue-amount">${invoice.amount}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'bulk-operations' && (
          <BulkInvoiceOperations />
        )}

        {activeTab === 'templates' && (
          <InvoiceTemplates />
        )}
      </div>

      {/* Invoice Preview Modal - Component removed */}
    </div>
  );
};

export default EnhancedInvoiceDashboard;
